import { useState, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Firestore, collection, query, where, getDocs } from 'firebase/firestore';
import { GuestListEntry } from '../../types';
import { UserPlus, Mail, X, Upload, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface GuestListTabProps {
  unitId: string;
  guestList: GuestListEntry[];
  setGuestList: (guestList: GuestListEntry[]) => void;
  canEdit: boolean;
  currentUser: any;
  db: Firestore;
  confirmAction: (options: any) => Promise<any>;
  formatDate: (date: Date) => string;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  Card: any;
  CardContent: any;
  CardDescription: any;
  CardHeader: any;
  CardTitle: any;
  Button: any;
  Input: any;
  Label: any;
  Badge: any;
  Select?: any;
  SelectTrigger?: any;
  SelectContent?: any;
  SelectItem?: any;
  SelectValue?: any;
  Dialog?: any;
  DialogContent?: any;
  DialogHeader?: any;
  DialogTitle?: any;
  DialogDescription?: any;
  DialogFooter?: any;
}

export function GuestListTab({
  unitId,
  guestList,
  setGuestList,
  canEdit,
  currentUser,
  db,
  confirmAction,
  formatDate,
  showSuccess,
  showError,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Badge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
}: GuestListTabProps) {
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [addingGuest, setAddingGuest] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [hasHeaders, setHasHeaders] = useState(true);
  const [fieldMapping, setFieldMapping] = useState<{ firstName: string; lastName: string; email: string }>({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<{
    imported: number;
    skipped: number;
    failed: Array<{ email: string; reason: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddGuest = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!guestEmail.trim()) {
      showError('Please enter an email address');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    // Check if email is already in guest list
    const existingGuest = guestList.find((g) => g.email === guestEmail.toLowerCase());
    if (existingGuest) {
      showError('This email is already in the guest list');
      return;
    }

    setAddingGuest(true);
    try {
      // Check if user exists with this email
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', guestEmail.toLowerCase())
      );
      const usersSnapshot = await getDocs(usersQuery);
      const userId = !usersSnapshot.empty ? usersSnapshot.docs[0].id : undefined;

      // Create new guest list entry with proper timestamp
      const newGuest: GuestListEntry = {
        id: Math.random().toString(36).substring(2, 15),
        name: guestFirstName.trim() || guestLastName.trim() 
          ? `${guestFirstName.trim()} ${guestLastName.trim()}`.trim() 
          : null, // Keep for backward compatibility
        email: guestEmail.toLowerCase(),
        userId: userId || null,
        addedAt: new Date(),
        addedBy: currentUser?.uid || null,
        source: 'manual' as const,
      };
      
      // Only add firstName and lastName if they have values (not empty strings)
      if (guestFirstName.trim()) {
        newGuest.firstName = guestFirstName.trim();
      }
      if (guestLastName.trim()) {
        newGuest.lastName = guestLastName.trim();
      }

      // Get fresh unit data to ensure we have the latest guestList
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (!unitDoc.exists()) {
        throw new Error('Unit not found');
      }

      const currentGuestList = unitDoc.data().guestList || [];

      // Update the guest list
      const updatedGuestList = [...currentGuestList, newGuest];

      await updateDoc(doc(db, 'units', unitId), {
        guestList: updatedGuestList,
      });

      // Add to user's guest subscriptions if user exists
      if (userId) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const existingSubscriptions = userData.guestSubscriptions || [];

            // Check if already subscribed
            const alreadySubscribed = existingSubscriptions.some(
              (sub: any) => sub.unitId === unitId
            );

            if (!alreadySubscribed) {
              const guestSubscription = {
                unitId: unitId,
                subscribedAt: new Date(),
              };
              const updatedSubscriptions = [...existingSubscriptions, guestSubscription];
              await updateDoc(doc(db, 'users', userId), {
                guestSubscriptions: updatedSubscriptions,
              });
            }
          }
        } catch (error) {
          console.error('Error adding guest subscription:', error);
          // Don't fail if user subscription update fails
        }
      }

      setGuestList(updatedGuestList);
      setGuestFirstName('');
      setGuestLastName('');
      setGuestEmail('');
      showSuccess('Guest added successfully');
    } catch (error) {
      console.error('Error adding guest:', error);
      showError('Failed to add guest to list');
    } finally {
      setAddingGuest(false);
    }
  };

  const parseCSV = (text: string): string[][] => {
    const lines: string[][] = [];
    let currentLine: string[] = [];
    let currentField = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentField += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        currentLine.push(currentField.trim());
        currentField = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        if (currentField || currentLine.length > 0) {
          currentLine.push(currentField.trim());
          currentField = '';
        }
        if (currentLine.length > 0) {
          lines.push(currentLine);
          currentLine = [];
        }
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
      } else {
        currentField += char;
      }
    }

    if (currentField || currentLine.length > 0) {
      currentLine.push(currentField.trim());
    }
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      showError('Please select a CSV file');
      return;
    }

    setCsvFile(file);
    setImportSummary(null);
    
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      
      if (rows.length === 0) {
        showError('CSV file is empty');
        return;
      }

      // Determine headers and data based on hasHeaders checkbox
      let headers: string[];
      let dataRows: string[][];
      
      if (hasHeaders && rows.length > 0) {
        headers = rows[0];
        dataRows = rows.slice(1);
      } else {
        // No headers - use column indices as headers (Column 1, Column 2, etc.)
        if (rows.length > 0) {
          const maxColumns = Math.max(...rows.map(row => row.length));
          headers = Array.from({ length: maxColumns }, (_, i) => `Column ${i + 1}`);
          dataRows = rows;
        } else {
          headers = [];
          dataRows = [];
        }
      }

      setCsvHeaders(headers);
      setCsvData(dataRows);

      // Auto-detect field mapping
      const autoMapping: { firstName: string; lastName: string; email: string } = {
        firstName: '',
        lastName: '',
        email: '',
      };

      headers.forEach((header) => {
        const lowerHeader = header.toLowerCase().trim();
        // Check for first name
        if ((lowerHeader.includes('first') && lowerHeader.includes('name')) || 
            (lowerHeader === 'first' || lowerHeader.startsWith('first'))) {
          if (!autoMapping.firstName) {
            autoMapping.firstName = header;
          }
        }
        // Check for last name
        else if ((lowerHeader.includes('last') && lowerHeader.includes('name')) || 
                 (lowerHeader === 'last' || lowerHeader.startsWith('last'))) {
          if (!autoMapping.lastName) {
            autoMapping.lastName = header;
          }
        }
        // Check for email
        else if (lowerHeader.includes('email')) {
          if (!autoMapping.email) {
            autoMapping.email = header;
          }
        }
      });
      
      // If email not found by keyword, look for columns that look like emails
      if (!autoMapping.email) {
        headers.forEach((header) => {
          if (header.includes('@') && header.includes('.') && header.length > 5 && header.length < 100) {
            if (!autoMapping.email) {
              autoMapping.email = header;
            }
          }
        });
      }
      
      // If still no email found, try to find the column that contains emails in the data
      if (!autoMapping.email && dataRows.length > 0) {
        for (let colIdx = 0; colIdx < headers.length; colIdx++) {
          const sampleValues = dataRows.slice(0, Math.min(3, dataRows.length)).map(row => row[colIdx] || '');
          const emailLikeCount = sampleValues.filter(val => 
            val.includes('@') && val.includes('.') && val.length > 5
          ).length;
          
          if (emailLikeCount >= Math.min(2, sampleValues.length)) {
            autoMapping.email = headers[colIdx];
            break;
          }
        }
      }

      setFieldMapping(autoMapping);
    } catch (error) {
      console.error('Error reading CSV file:', error);
      showError('Failed to read CSV file');
    }
  };

  const handleHasHeadersChange = (checked: boolean) => {
    setHasHeaders(checked);
    
    // Re-process the file when header option changes
    if (csvFile) {
      csvFile.text().then((text) => {
        const rows = parseCSV(text);
        
        if (rows.length === 0) {
          return;
        }

        let headers: string[];
        let dataRows: string[][];
        
        if (checked && rows.length > 0) {
          headers = rows[0];
          dataRows = rows.slice(1);
        } else {
          if (rows.length > 0) {
            const maxColumns = Math.max(...rows.map(row => row.length));
            headers = Array.from({ length: maxColumns }, (_, i) => `Column ${i + 1}`);
            dataRows = rows;
          } else {
            headers = [];
            dataRows = [];
          }
        }

        setCsvHeaders(headers);
        setCsvData(dataRows);

        // Auto-detect field mapping
        const autoMapping: { firstName: string; lastName: string; email: string } = {
          firstName: '',
          lastName: '',
          email: '',
        };

        headers.forEach((header) => {
          const lowerHeader = header.toLowerCase().trim();
          // Check for first name
          if ((lowerHeader.includes('first') && lowerHeader.includes('name')) || 
              (lowerHeader === 'first' || lowerHeader.startsWith('first'))) {
            if (!autoMapping.firstName) {
              autoMapping.firstName = header;
            }
          }
          // Check for last name
          else if ((lowerHeader.includes('last') && lowerHeader.includes('name')) || 
                   (lowerHeader === 'last' || lowerHeader.startsWith('last'))) {
            if (!autoMapping.lastName) {
              autoMapping.lastName = header;
            }
          }
          // Check for email
          else if (lowerHeader.includes('email')) {
            if (!autoMapping.email) {
              autoMapping.email = header;
            }
          }
        });
        
        if (!autoMapping.email) {
          headers.forEach((header) => {
            if (header.includes('@') && header.includes('.') && header.length > 5 && header.length < 100) {
              if (!autoMapping.email) {
                autoMapping.email = header;
              }
            }
          });
        }
        
        if (!autoMapping.email && dataRows.length > 0) {
          for (let colIdx = 0; colIdx < headers.length; colIdx++) {
            const sampleValues = dataRows.slice(0, Math.min(3, dataRows.length)).map(row => row[colIdx] || '');
            const emailLikeCount = sampleValues.filter(val => 
              val.includes('@') && val.includes('.') && val.length > 5
            ).length;
            
            if (emailLikeCount >= Math.min(2, sampleValues.length)) {
              autoMapping.email = headers[colIdx];
              break;
            }
          }
        }

        setFieldMapping(autoMapping);
      }).catch((error) => {
        console.error('Error re-reading CSV file:', error);
      });
    }
  };

  const handleImportGuests = async () => {
    if (!fieldMapping.email) {
      showError('Please map the Email field');
      return;
    }

    if (csvData.length === 0) {
      showError('No data to import');
      return;
    }

    setImporting(true);
    const summary = {
      imported: 0,
      skipped: 0,
      failed: [] as Array<{ email: string; reason: string }>,
    };

    try {
      const emailIdx = csvHeaders.indexOf(fieldMapping.email);
      const firstNameIdx = fieldMapping.firstName ? csvHeaders.indexOf(fieldMapping.firstName) : -1;
      const lastNameIdx = fieldMapping.lastName ? csvHeaders.indexOf(fieldMapping.lastName) : -1;

      if (emailIdx === -1) {
        showError('Invalid field mapping. Please ensure the email field is mapped correctly.');
        setImporting(false);
        return;
      }

      // Get fresh unit data
      const unitDoc = await getDoc(doc(db, 'units', unitId));
      if (!unitDoc.exists()) {
        throw new Error('Unit not found');
      }

      const currentGuestList = unitDoc.data().guestList || [];
      const existingEmails = new Set(currentGuestList.map((g: GuestListEntry) => g.email.toLowerCase()));

      // Process each row
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const newGuests: GuestListEntry[] = [];
      const userIdsToCheck = new Set<string>();

      for (const row of csvData) {
        const email = (row[emailIdx] || '').trim().toLowerCase();
        const firstName = firstNameIdx >= 0 ? (row[firstNameIdx] || '').trim() : '';
        const lastName = lastNameIdx >= 0 ? (row[lastNameIdx] || '').trim() : '';

        // Validate email
        if (!email || !emailRegex.test(email)) {
          summary.failed.push({ email: email || '(empty)', reason: 'Invalid email format' });
          summary.skipped++;
          continue;
        }

        // Check if already exists
        if (existingEmails.has(email)) {
          summary.skipped++;
          continue;
        }

        // Check if user exists with this email
        const usersQuery = query(
          collection(db, 'users'),
          where('email', '==', email)
        );
        const usersSnapshot = await getDocs(usersQuery);
        const userId = !usersSnapshot.empty ? usersSnapshot.docs[0].id : undefined;

        const newGuest: GuestListEntry = {
          id: Math.random().toString(36).substring(2, 15),
          name: firstName || lastName 
            ? `${firstName} ${lastName}`.trim() 
            : null, // Keep for backward compatibility
          email: email,
          userId: userId || null,
          addedAt: new Date(),
          addedBy: currentUser?.uid || null,
          source: 'manual' as const,
        };
        
        // Only add firstName and lastName if they have values (not empty strings)
        if (firstName && firstName.trim()) {
          newGuest.firstName = firstName.trim();
        }
        if (lastName && lastName.trim()) {
          newGuest.lastName = lastName.trim();
        }

        newGuests.push(newGuest);
        existingEmails.add(email);
        if (userId) {
          userIdsToCheck.add(userId);
        }
      }

      if (newGuests.length === 0) {
        showError('No valid guests to import. All guests may already be in the list or have invalid emails.');
        setImporting(false);
        return;
      }

      // Update the guest list
      const updatedGuestList = [...currentGuestList, ...newGuests];
      await updateDoc(doc(db, 'units', unitId), {
        guestList: updatedGuestList,
      });

      // Add to user's guest subscriptions if users exist
      for (const userId of userIdsToCheck) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const existingSubscriptions = userData.guestSubscriptions || [];

            const alreadySubscribed = existingSubscriptions.some(
              (sub: any) => sub.unitId === unitId
            );

            if (!alreadySubscribed) {
              const guestSubscription = {
                unitId: unitId,
                subscribedAt: new Date(),
              };
              const updatedSubscriptions = [...existingSubscriptions, guestSubscription];
              await updateDoc(doc(db, 'users', userId), {
                guestSubscriptions: updatedSubscriptions,
              });
            }
          }
        } catch (error) {
          console.error('Error adding guest subscription:', error);
          // Don't fail if user subscription update fails
        }
      }

      summary.imported = newGuests.length;
      setGuestList(updatedGuestList);
      setImportSummary(summary);
      showSuccess(`Successfully imported ${summary.imported} guest${summary.imported !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Error importing guests:', error);
      showError('Failed to import guests. Check the console for details.');
    } finally {
      setImporting(false);
    }
  };

  const handleRemoveGuest = async (guestId: string, guestEmailAddr: string) => {
    const result = await confirmAction({
      title: 'Remove Guest?',
      text: `${guestEmailAddr} will no longer receive invitations to future events.`,
      confirmButtonText: 'Remove',
    });

    if (result.isConfirmed) {
      try {
        const guestToRemove = guestList.find((g) => g.id === guestId);
        if (!guestToRemove) return;

        // Update the guest list by filtering out the removed guest
        const updatedGuestList = guestList.filter((g) => g.id !== guestId);
        await updateDoc(doc(db, 'units', unitId), {
          guestList: updatedGuestList,
        });

        // Remove from user's guest subscriptions if user exists
        if (guestToRemove.userId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', guestToRemove.userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const guestSubscriptions = userData.guestSubscriptions || [];
              const updatedSubscriptions = guestSubscriptions.filter(
                (s: any) => s.unitId !== unitId
              );

              await updateDoc(doc(db, 'users', guestToRemove.userId), {
                guestSubscriptions: updatedSubscriptions,
              });
            }
          } catch (error) {
            console.error('Error removing guest subscription:', error);
            // Don't fail if user subscription update fails
          }
        }

        setGuestList(updatedGuestList);
        showSuccess('Guest removed from list');
      } catch (error) {
        console.error('Error removing guest:', error);
        showError('Failed to remove guest');
      }
    }
  };

  return (
    <div className="space-y-6">
      {canEdit && (
        <Card>
          <CardHeader>
            <CardTitle>Add Guest</CardTitle>
            <CardDescription>
              Add people who should receive invitations to future events
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddGuest} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="guestFirstName">First Name</Label>
                  <Input
                    id="guestFirstName"
                    type="text"
                    placeholder="John (optional)"
                    value={guestFirstName}
                    onChange={(e: any) => setGuestFirstName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guestLastName">Last Name</Label>
                  <Input
                    id="guestLastName"
                    type="text"
                    placeholder="Smith (optional)"
                    value={guestLastName}
                    onChange={(e: any) => setGuestLastName(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="guestEmail">Email Address *</Label>
                <Input
                  id="guestEmail"
                  type="email"
                  placeholder="guest@example.com"
                  value={guestEmail}
                  onChange={(e: any) => setGuestEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={addingGuest}>
                <UserPlus className="mr-2 h-4 w-4" />
                {addingGuest ? 'Adding...' : 'Add to Guest List'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Guest List</CardTitle>
              <CardDescription>
                People who will receive invitations to future events. Guests can be added manually
                or automatically when they RSVP and opt-in to future events.
              </CardDescription>
            </div>
            {canEdit && Select && SelectTrigger && SelectContent && SelectItem && SelectValue && Dialog && DialogContent && DialogHeader && DialogTitle && DialogDescription && DialogFooter && (
              <Button onClick={() => setImportModalOpen(true)} variant="outline">
                <Upload className="mr-2 h-4 w-4" />
                Import
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {guestList.length > 0 ? (
            <div className="space-y-2">
              {guestList.map((guest) => (
                <div
                  key={guest.id}
                  className="flex items-center justify-between rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="font-semibold">
                      {(guest.firstName || guest.lastName)
                        ? `${guest.firstName || ''} ${guest.lastName || ''}`.trim()
                        : guest.name || guest.email}
                      {(guest.firstName || guest.lastName || guest.name) && (
                        <span className="text-sm text-muted-foreground ml-2">
                          ({guest.email})
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Added {formatDate(guest.addedAt)} via{' '}
                      {guest.source === 'rsvp' ? 'RSVP' : 'manual entry'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {guest.userId && (
                      <Badge variant="secondary" className="text-xs">
                        Registered User
                      </Badge>
                    )}
                    <Badge variant={guest.source === 'rsvp' ? 'default' : 'secondary'}>
                      {guest.source === 'rsvp' ? 'RSVP Opt-in' : 'Manual'}
                    </Badge>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveGuest(guest.id, guest.email)}
                        title="Remove guest"
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No guests in your list yet</p>
              <p className="text-sm mt-1">
                Add guests manually or they will be added automatically when RSVPing to events
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {Dialog && DialogContent && DialogHeader && DialogTitle && DialogDescription && DialogFooter && Select && SelectTrigger && SelectContent && SelectItem && SelectValue && (
        <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Guests from CSV</DialogTitle>
              <DialogDescription>
                Upload a CSV file and map the columns to import guests. Guests will be added to the guest list.
              </DialogDescription>
            </DialogHeader>

            {!importSummary ? (
              <>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="csv-file">CSV File</Label>
                    <Input
                      id="csv-file"
                      type="file"
                      accept=".csv"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Select a CSV file containing guest information (first name, last name, and email)
                    </p>
                  </div>

                  {csvFile && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="has-headers"
                        checked={hasHeaders}
                        onChange={(e) => handleHasHeadersChange(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                      <Label htmlFor="has-headers" className="text-sm font-normal cursor-pointer">
                        First row contains column headings
                      </Label>
                    </div>
                  )}

                  {csvData.length > 0 && csvHeaders.length > 0 && (
                    <>
                      <div>
                        <Label>Field Mapping</Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Map CSV columns to the required fields (email is required, first name and last name are optional)
                        </p>
                        <div className="space-y-2">
                          <div>
                            <Label htmlFor="firstName-map" className="text-sm">
                              First Name (Optional)
                            </Label>
                            <Select
                              value={fieldMapping.firstName || '__none__'}
                              onValueChange={(value: string) =>
                                setFieldMapping({ ...fieldMapping, firstName: value === '__none__' ? '' : value })
                              }
                            >
                              <SelectTrigger id="firstName-map">
                                <SelectValue placeholder="Select column (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {csvHeaders.map((header) => (
                                  <SelectItem key={header} value={header}>
                                    {header}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="lastName-map" className="text-sm">
                              Last Name (Optional)
                            </Label>
                            <Select
                              value={fieldMapping.lastName || '__none__'}
                              onValueChange={(value: string) =>
                                setFieldMapping({ ...fieldMapping, lastName: value === '__none__' ? '' : value })
                              }
                            >
                              <SelectTrigger id="lastName-map">
                                <SelectValue placeholder="Select column (optional)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">None</SelectItem>
                                {csvHeaders.map((header) => (
                                  <SelectItem key={header} value={header}>
                                    {header}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="email-map" className="text-sm">
                              Email Address *
                            </Label>
                            <Select
                              value={fieldMapping.email}
                              onValueChange={(value: string) =>
                                setFieldMapping({ ...fieldMapping, email: value })
                              }
                            >
                              <SelectTrigger id="email-map">
                                <SelectValue placeholder="Select column" />
                              </SelectTrigger>
                              <SelectContent>
                                {csvHeaders.map((header) => (
                                  <SelectItem key={header} value={header}>
                                    {header}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg bg-muted p-3">
                        <p className="text-sm font-medium mb-1">Preview</p>
                        <p className="text-xs text-muted-foreground">
                          Found {csvData.length} row{csvData.length !== 1 ? 's' : ''} to import
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setImportModalOpen(false);
                      setCsvFile(null);
                      setCsvData([]);
                      setCsvHeaders([]);
                      setFieldMapping({ firstName: '', lastName: '', email: '' });
                      setImportSummary(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleImportGuests} 
                    disabled={importing || !csvFile || csvData.length === 0 || !fieldMapping.email}
                  >
                    {importing ? 'Importing...' : 'Import Guests'}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <div className="space-y-4 py-4">
                  <div className="rounded-lg border p-4 space-y-3">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">Imported: {importSummary.imported}</span>
                    </div>
                    <div className="flex items-center gap-2 text-yellow-600">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Skipped: {importSummary.skipped}</span>
                    </div>
                    {importSummary.failed.length > 0 && (
                      <div className="flex items-start gap-2 text-red-600">
                        <XCircle className="h-5 w-5 mt-0.5" />
                        <div className="flex-1">
                          <span className="font-medium">Failed: {importSummary.failed.length}</span>
                          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                            {importSummary.failed.map((failure, idx) => (
                              <div key={idx} className="text-xs">
                                <span className="font-medium">{failure.email}:</span> {failure.reason}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    onClick={() => {
                      setImportModalOpen(false);
                      setCsvFile(null);
                      setCsvData([]);
                      setCsvHeaders([]);
                      setFieldMapping({ firstName: '', lastName: '', email: '' });
                      setImportSummary(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                  >
                    Close
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

